import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './modal';
import { Button } from './button';
import { useLanguage } from '../../context/LanguageContext';
import { useTenant } from '../../context/TenantContext';
import PrivacyPolicyIcon from '../../assets/images/icons/cookies-icon.png';

/**
 * CookiePolicyModal
 * Props:
 * - open (bool)
 * - onOpenChange (fn)
 * - onAccept (fn) -> called when user accepts (should handle storage & API)
 * - onDeny (fn) -> called when user denies (should handle storage & API)
 */
const CookiePolicyModal = ({ open, onOpenChange, onAccept, onDeny }) => {
    const { t } = useLanguage();
    const { tenant } = useTenant();
    const navigate = useNavigate();

    const handleAccept = () => {
        if (onAccept) onAccept();
        onOpenChange(false);
    };

    const handleDeny = () => {
        if (onDeny) onDeny();
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[360px] h-[246px] fixed bottom-[30px] left-1/2 -translate-x-1/2 translate-y-0 top-auto rounded-2xl data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom px-3 py-2">
                <DialogHeader className="flex-row mt-2">
                    <div className="flex items-start justify-center mr-3 h-[100px] w-[100px]">
                        <img src={PrivacyPolicyIcon} alt="" aria-hidden="true" className="block w-full flex-shrink-0 object-contain" />
                    </div>
                    <div className="text-left text-xs font-normal">
                        <DialogTitle className='font-bold text-[13px] text-theme-secondary mb-1 text-left'>
                            {t('cookieModal.title') || 'Your Privacy Matters'}
                        </DialogTitle>
                        <DialogDescription className='text-xs font-medium text-color[hsla(0, 0%, 42%, 1)] leading-tight text-left'>
                            {t('cookieModal.description') || 'We use cookies to enhance your experience and analyse how our site is used.'}
                        </DialogDescription>
                        <div className='mt-2'><a
                            href="#cookie-policy"
                            className="text-theme-primary hover:text-blue-700 underline text-[11px] font-medium focus:outline-none focus:ring-2 focus:ring-theme-primary focus:ring-offset-2 rounded px-1"
                            onClick={(e) => {
                                e.preventDefault();
                                const cookieConfig = tenant?.tenantcontentconfig?.cookies?.cookie;
                                if (cookieConfig?.isLink && cookieConfig?.link) {
                                    // External link
                                    window.open(cookieConfig.link, cookieConfig.target || '_blank');
                                } else {
                                    // Internal navigation to /cookiepolicy
                                    const tenanturlname = tenant?.tenanturlname || 'dev-demo';
                                    navigate(`/${tenanturlname}/cookiepolicy`);
                                }
                            }}
                        >
                            {t('cookieModal.readPolicy') || 'Read Cookie Policy'}
                        </a></div>
                    </div>
                </DialogHeader>
                <DialogDescription className="text-center text-gray-600 text-sm mb-3">

                </DialogDescription>
                <div className="space-y-2 mt-6">
                    <Button
                        type="button"
                        variant="primary"
                        size="lg"
                        onClick={handleAccept}
                        tabIndex={0}
                        className="rounded-[40px] h-[40px] text-xs font-medium focus:ring-2 focus:ring-theme-primary focus:ring-offset-2"
                    >
                        {t('cookieModal.acceptAll') || 'Accept All Cookies'}
                    </Button>
                    <Button
                        type="button"
                        onClick={handleDeny}
                        variant="secondary"
                        size="lg"
                        tabIndex={0}
                        className="rounded-[40px] h-[40px] text-xs font-medium focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                    >
                        {t('cookieModal.deny') || 'Deny'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CookiePolicyModal;
